import { GetRESTData, getBaseSearchURL, getDigest, RemoveSpecialCharsFromQuery } from "../../utils/searchHelpers";
import IRefiners from "../../Models/IRefiners";
import IRefiner from "../../Models/IRefiner";
import { EksamenFilterGroupOrder, CourseTypeChechboxOrder, subCourseTypeChechboxes  } from '../../utils/sortingLists';
import { Log } from '../../utils/logging';
import { getUrlParameter } from '../../utils/urlParamHelper';
import { ConvertStringToSearchToken } from '../../utils/refinerHelpers';


const selectedProperties = [
    "Url",
    "Title",
    "ExCourseCode",
    "ExCatalogName",
    "ExCatalogTypeName",
    "ExPeriodName",
    "ExDocumentRepositoryCode",
    "ExUrl",
    "ExUrlEksamen",
    "ExUrlForb",
    "ExUrlForhaandssensur",
    "ExFileExtension",
    "ExFileExtensionForb",
    "ExFileExtensionForhaandssensur",
    "ExFilename",
    "ExFilenameForb",
    "ExFilenameForhaandssensur",
    "ExIsPasswordProtected",
    "ExIsPasswordProtectedForb",
    "ExMaalform",
    "ExCourseType",
    "ExCourseLaereplanURL",
    "ExCourseLaereplanKode",
    "ExListID",
    "ExListItemID",
    "ExSPSiteURL",
    "ExUIVersionStringOWSTEXT",
    "ExUniqueID",
    "ExListItemIDForb",
    "ExUniqueIDForb",
    "ExFileExtensionVedlegg",
    "ExFileExtensionVedleggForb",
    "ExFilenameVedlegg",
    "ExFilenameVedleggForb",
    "ExIsPasswordProtectedVedlegg",
    "ExIsPasswordProtectedVedleggForb",
    "ExListItemIDVedlegg",
    "ExListItemIDVedleggForb",
    "ExUniqueIDVedlegg",
    "ExUniqueIDVedleggForb",
    "ExUrlVedlegg",
    "ExUrlVedleggForb",
    "ExVedlegg",
    "ExVisningstidspunkt",
    "ExPassord",
    "ExKL",
    "ExAnsvarstype",
]


const properties = [
    "SourceName:Doklager",
    "SourceLevel:SPSite"
]

export async function getResults(query: string, pageNumber: number, refiners?: IRefiners[], rowLimit?: number) {
    //Getting different search url based on which environment we currently are in
    const baseUrl = getBaseSearchURL();

    //Setting default query to be all items
    let queryText = query !== "" ? query : "*";
    queryText = RemoveSpecialCharsFromQuery(queryText);

    let selectedPropertiesString = encodeURI(selectedProperties.join(','));
    let propertiesString = encodeURI(properties.join(','));
    let queryString = encodeURI(`${baseUrl}/_api/search/query?querytext='${queryText}'`);
    let startRow = (pageNumber - 1) * 10;
    let safeRowLimit = rowLimit ? rowLimit : 10;
    let refinementFilter = encodeURI(generateRefinementFilters(refiners));
    
    // Get sort parameter from URL
    const sortByParam = getUrlParameter('sortby');
    let sortlist;
    
    if (sortByParam === 'date-desc') {
        sortlist = "ExVisningstidspunkt:descending";
    } else if (sortByParam === 'date-asc') {
        sortlist = "ExVisningstidspunkt:ascending";
    } else if (sortByParam === 'relevans') {
        sortlist = "Rank:descending"; // Use relevance ranking
    } else {
        sortlist = "Rank:descending"; // Default to relevance
    }
    Log("Sortlist is " + sortlist);
    
    queryString += `&selectproperties='${encodeURIComponent(selectedPropertiesString)}'`
    queryString += `&trimduplicates=false`
    queryString += `&culture=1044`
    queryString += `&enablequeryrules=true`
    //queryString += `&processbestbets=true`
    queryString += `&refiners='ExTestType(sort=name/ascending)%2cExCatalogTypeName(sort=name/ascending)%2cExPeriodName(sort=name/descending)'`
    queryString += refinementFilter;
    queryString += `&properties='${propertiesString}'`
    //queryString += `&hithighlightedproperties='Title,description'`
    queryString += `&clienttype='AllResultsQuery'`
    if(sortlist){
        queryString += `&sortlist='${sortlist}'`;
    }
    queryString += `&startrow=${startRow}`;
    queryString += `&rowlimit=${safeRowLimit}`
    let response;
    //if(baseUrl === "https://udirsok-dev.udir.no" || baseUrl === "https://qa-sok.udir.no" || baseUrl === "https://sok.udir.no"){
    if(false){
        queryString += `&QueryTemplatePropertiesUrl='spfile://webroot/queryparametertemplate-Doklager.xml'`;
        const digest = await getDigest(baseUrl);
        Log("using queryparametertemplate.xml");
        response = await fetch(queryString, {
            headers: {
                'Accept': 'application/json',
                'X-RequestDigest': digest
            }
        });
    }else{
        response = await fetch(queryString, {
            headers: {
                'Accept': 'application/json'
            }
        });
    }
    Log(queryString);
    let results = await response.json();
    let prossesedResults = GetRESTData(results, queryString, processRefiners);
    Log(prossesedResults);
    return prossesedResults;
}


export function generateRefinementFilters(refiners: IRefiners[] | undefined) {
    const dateNow = new Date();
    const dateNowIso = dateNow.toISOString();
    let dateNowIsoFQL = `ExVisningstidspunkt:range(min,${dateNowIso},to="le")`;
    if(!refiners) {
        //return `&refinementFilters='ExVisningstidspunkt:range(min,2019-12-13T16:00:00.0000000Z,to="le")'`;
        return `&refinementFilters='${dateNowIsoFQL}'`;
    }
    let refinerStrings: string[][] = [];
    refiners.forEach(refiner => {
        // NB: ExPeriodName skal brukes direkte i FQL refinementFilters. Et tidligere
        // forsøk (UDIRSOK-726) mappet den til ExPeriod, men ExPeriod matcher ingen
        // dokumenter i refinementFilters - verifisert mot både prod og QA 02.09.2026.
        let checkedRefinerValues = refiner.Entries.filter(refinerValue => refinerValue.checked);
        let checkedRefinerStrings = checkedRefinerValues.map(val => `${refiner.Name}:${val.RefinementToken}`)
        // Eksamensveiledninger er ikke knyttet til en eksamensperiode og skal ALDRI
        // filtreres bort av År-filteret (avklart m/Magnus 27.03.2026). OR inn
        // eksamensveiledninger i periode-gruppa, så de alltid slipper gjennom.
        if (refiner.Name === 'ExPeriodName' && checkedRefinerStrings.length > 0) {
            checkedRefinerStrings.push(`ExCatalogTypeName:${ConvertStringToSearchToken('Eksamensveiledninger')}`);
        }
        refinerStrings.push(checkedRefinerStrings);
    });
    refinerStrings = refinerStrings.filter(list => list.length > 0);

    if(refinerStrings.length  === 0) {
        Log("generateRefinementFilters-0");
        return `&refinementFilters='${dateNowIsoFQL}'`;
    } else if(refinerStrings.length === 1) {
        Log("generateRefinementFilters-1");
        return `&refinementFilters='AND(${dateNowIsoFQL},${mapRefinersToStrings(refinerStrings[0])})'`;
    } else {
        Log("generateRefinementFilters-ELSE");
        return `&refinementFilters='AND(${dateNowIsoFQL},${refinerStrings.map( refinerString => mapRefinersToStrings(refinerString)).join(',')})'`
    }

}

function mapRefinersToStrings(refinerElementStrings) {
    if(refinerElementStrings.length === 0) {
        return "";
    } else if(refinerElementStrings.length === 1) {
        return `${refinerElementStrings[0]}`;
    } else {
        return `OR(${refinerElementStrings.join(',')})`
    }
}

function processRefiners(searchRefiners) {
    if (null === searchRefiners) return null;

    let refiners = searchRefiners.Refiners
    let list:IRefiners[] = [];
    Log(refiners);
    refiners.forEach(function (refiner) {
        if(refiner.Name === 'ExPeriodName') {
            list.push(processPeriodNameRefiner(refiner));
        } else {
            list.push({
                Name: refiner.Name,
                Entries: refiner.Entries
            });
        }
    });

    const sortedList = list.sort(sortRefinerGroups);
    //Special handling of the nivaa refiner: custom sorting and indentation(sub-checkboxes)
    let subCourseTypeRefiner = sortedList.filter(refiner => refiner.Name ==="ExCatalogTypeName")[0]
    if (subCourseTypeRefiner) {
        subCourseTypeRefiner.Entries.sort((a, b) => sortRefiners(a, b, CourseTypeChechboxOrder));
                subCourseTypeRefiner.Entries.forEach((subCourseType: IRefiner) => {
                    if (subCourseTypeChechboxes.indexOf(subCourseType.RefinementName) !== -1) {
                        subCourseType.indentation = true;
                        subCourseType.level = 1;
                        subCourseType.hidden = true;
                        subCourseType.parent = "Eksamensoppgaver"
                    } else{
                        subCourseType.level = 0;
                    }
                });

    }
    return sortedList;
}

export function processPeriodNameRefiner(refiner) {
    try {
        for (var entry in refiner.Entries) {
            var value = refiner.Entries[entry].RefinementValue;
            var newValue = value.split('-')[1] + " " + value.split('-')[0];
            newValue = newValue.replace('1 ', 'Vår ');
            newValue = newValue.replace('2 ', 'Høst ');
            newValue = newValue.replace('Alle-', 'Alle');
            //refiner.Entries[entry].RefinementValue = newValue;
            refiner.Entries[entry].RefinementName = newValue;
        }
    } catch (err) {
        Log("Could not change date refinement values.");
    }
    return refiner;
}

function sortRefinerGroups(a: IRefiners, b: IRefiners) {
    return EksamenFilterGroupOrder.indexOf(a.Name) - EksamenFilterGroupOrder.indexOf(b.Name);
}

function sortRefiners(a: IRefiner, b: IRefiner, orderList: string[]) {
    let aOrder = orderList.indexOf(a.RefinementName);
    let bOrder = orderList.indexOf(b.RefinementName);

    //Check if they are defined in the sortlist, if not, send them to the back
    if (aOrder !== -1 && bOrder !== -1) {
        return aOrder - bOrder;
    } else if (aOrder === -1 && bOrder === -1) {
        return a.RefinementName.localeCompare(b.RefinementName);
    }
    else if (aOrder === -1) {
        return 1
    } else if (bOrder === -1) {
        return -1
    } else {
        return a.RefinementName.localeCompare(b.RefinementName);
    }
}